declare let API_HREF: string;
// Dependencies
import { Loader } from '@googlemaps/js-api-loader';
import axios from 'axios';
import clsx from 'clsx';
import i18next from 'i18next';
import React, { ComponentType, FC, lazy, ReactElement, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
// Core & Lab
import * as locales from '@mui/material/locale';
import { createTheme, ThemeProvider } from '@mui/material/styles';
// Components
import Loading from '../component/loading/loading';
// Header
const Header = lazy(() => import('../component/header/header'));
// Navigation
const Navigation = lazy(() => import('../component/navigation/navigation'));
// Utils
import ErrorBoundary from './errorBoundary';
// Functions
import getMuiLanguage from '../functions/getMuiLanguage';
// Actions
import { setConfig, setGoogle } from '../component/header/redux/actions';
import { setList, setListLocales } from '../component/locale/redux/actions';
import { setPermissions, setPreferences, setProfile } from '../component/profile/redux/actions';
// Types
import { AppState } from '../reducers';

type PrivateRouteProps = {
    component: ComponentType
};

const FULL_WIDTH_MODULES = ['organisation-edit', 'accommodation-stock-status', 'sales-receipt', 'purchases-receipt', 'account-settings', 'accommodation-event-stock'];

const PrivateRoute: FC<PrivateRouteProps> = ({ component: Component, ...rest }): ReactElement => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const open = useSelector((state: AppState) => state.navigation.open);
    const floating = useSelector((state: AppState) => state.navigation.floating);
    const client_id = useSelector((state: AppState) => state.header.client_id);
    const retrieved = useSelector((state: AppState) => state.profile.retrieved);
    const is_customer = useSelector((state: AppState) => state.profile.profile?.is_customer);
    const groups = useSelector((state: AppState) => state.profile.profile?.groups);
    const quotation_code = useSelector((state: AppState) => state.header.quotation_code);
    const user_id = localStorage.getItem('user_id');
    const token = localStorage.getItem('token');
    const location = useLocation();

    const handleHoverIn = () => {
        if (containerRef.current) {
            if(!FULL_WIDTH_MODULES.some(module => location.pathname.includes(module))) {
                containerRef.current.style.overflowY = 'auto';
                containerRef.current.style.paddingRight = '16px';
            }
        }
    };
    const handleHoverOut = () => {
        if (containerRef.current) {
            if(!FULL_WIDTH_MODULES.some(module => location.pathname.includes(module))) {
                containerRef.current.style.overflowX = 'hidden';
                containerRef.current.style.overflowY = 'hidden';
                containerRef.current.style.paddingRight = '24px';
            }
        }
    };
    useEffect(() => {
        if(groups && groups?.some(elem => elem.documentation_only)) {
            navigate('/documentation', { replace:true });
        }
    }, [groups]);
    if (user_id === null || token === null || is_customer) {
        return <Navigate replace to={'/login'} />;
    } else {
        if (client_id === null) {
            const result = /.([^.]+)/.exec(window.location.host);
            let customer_identifier = result !== null ? result[0] : 'vat-dev';
            let domain_name = window.location.host;
            if (window.location.host === 'localhost:3001') {
                customer_identifier = 'dev';
                domain_name = 'dev.facilitatrip.com';
                // domain_name = 'dev.tripmci.com';
                // customer_identifier = 'vat-dev';
                // domain_name = 'vat-dev.facilitatrip.com';
            }
            axios({
                method: 'GET',
                url: `${API_HREF}instances/?customer_identifier=${customer_identifier}&domain_name=${domain_name}`
            }).then((response) => {
                dispatch(setConfig(response.data));
                dispatch(setList(response.data.activated_locales));
                if (response.data.favicon !== undefined && response.data.favicon !== null && response.data.favicon.thumbnail_little !== undefined && response.data.favicon.url !== undefined) {
                    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
                    link.type = 'image/x-icon';
                    link.rel = 'shortcut icon';
                    link.href = (response.data.favicon.thumbnail_little !== null ? response.data.favicon.thumbnail_little : response.data.favicon.url);
                    document.getElementsByTagName('head')[0].appendChild(link);
                }
                let google_key = null;
                for (let i = 0; i < response.data.keys.length; i++) {
                    if (response.data.keys[i].identifier === 'google_api') {
                        google_key = response.data.keys[i].value;
                        break;
                    }
                }
                const loader = new Loader({
                    apiKey: google_key,
                    version: 'beta',
                    libraries: ['places']
                });
                loader
                .load()
                .then((google) => {
                    dispatch(setGoogle(google));
                })
                .catch(e => {
                    // do something
                });
                axios({
                    method: 'GET',
                    headers: {
                        'Authorization': `Token ${localStorage.getItem('token')}`,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Accept-Language': 'fr'
                    },
                    url: `${API_HREF}client/${response.data.client_id}/user/${user_id}/`
                }).then((response) => {
                    dispatch(setProfile(response.data));
                }).catch((error) => {
                    console.log('error : ', error);
                });
                axios({
                    method: 'GET',
                    headers: {
                        'Authorization': `Token ${localStorage.getItem('token')}`,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Accept-Language': 'fr'
                    },
                    url: `${API_HREF}client/${response.data.client_id}/user/${user_id}/permissions/?all=true`
                }).then((response) => {
                    dispatch(setPermissions(response.data));
                }).catch((error) => {
                    console.log('error : ', error);
                });
                axios({
                    method: 'GET',
                    url: `${API_HREF}client/${response.data.client_id}/user-table-preference/`,
                    headers: {
                        'Authorization': `Token ${token}`,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Accept-Language': 'fr'
                    },
                    params: {
                        limit: 10000
                    }
                }).then((response) => {
                    console.log('response : ', response.data);
                    dispatch(setPreferences(response.data.results));
                }).catch((error) => {
                    console.log('error : ', error);
                });
            }).catch((error) => {
                console.log('error : ', error);
            });
            axios({
                method: 'GET',
                url: `${API_HREF}locales/?ordering=id`
            }).then((response) => {
                dispatch(setListLocales(response.data));
            }).catch((error) => {
                console.log('error : ', error);
            });
            return <Loading />;
        }
        if (!retrieved) {
            return <Loading />;
        }
        const locales_cpy: { [key: string]: any } = locales;
        return (
            <ThemeProvider theme={createTheme({}, locales_cpy[getMuiLanguage(i18next.language)])}>
                <Header />
                <Navigation />
                {/* <main className={clsx('ft-utils-content', { ['ft-utils-contentShift']: open }, { ['ft-organisation-main']: location.pathname.includes('organisation-edit') }, { ['ft-accommodation-stock-status-main']: location.pathname.includes('accommodation-stock-status') })}> */}
                <main className={clsx('ft-utils-content', { ['ft-utils-contentBlocked']: floating }, { ['ft-utils-contentShift']: open }, { ['ft-organisation-main']: location.pathname.includes('organisation-edit') }, { ['ft-accommodation-stock-status-main']: location.pathname.includes('accommodation-stock-status') }, {['ft-accounting-main']: (location.pathname.includes('sales-receipt') || location.pathname.includes('purchases-receipt') || location.pathname.includes('account-settings') ) })} ref={containerRef} onMouseEnter={handleHoverIn} onMouseLeave={handleHoverOut}>
                    <ErrorBoundary>
                        <Component {...rest} />
                    </ErrorBoundary>
                </main>
            </ThemeProvider>
        );
    }
};

export default PrivateRoute;